import { useState } from "react";
import { useToast } from "@/components/ui/use-toast";
import { v4 as uuidV4 } from "uuid";
import {
  useArchiveGeneralContract,
  useDeleteGeneralContract,
  useUnstageGeneralContract,
} from "@/lib/api/generalContract";
import { createState, updateState } from "./useGeneralContractState";

export function useGeneralContractHandlers() {
  const { toast } = useToast();
  const [isArchiving, setIsArchiving] = useState(false);
  const [isReviewing, setIsReviewing] = useState(false);
  const [reviewModalId, setReviewModalId] = useState<undefined | string>();
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isUpdateOpen, setIsUpdateOpen] = useState(false);

  const archiveGeneralContract = useArchiveGeneralContract();
  const deleteGeneralContract = useDeleteGeneralContract();
  const unstageGeneralContract = useUnstageGeneralContract();

  const createPopulateWith: any = createState.useInputsState(
    (state: any) => state.populateWith
  );

  const updatePopulateWith: any = updateState.useInputsState(
    (state: any) => state.populateWith
  );

  const createClearForm: any = createState.useInputsState(
    (state: any) => state.clearForm
  );

  const updateClearForm: any = updateState.useInputsState(
    (state: any) => state.clearForm
  );

  const onArchive = async ({ id }: { id: string }) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await archiveGeneralContract({ id });
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to archive contract",
        description: "",
      });
    } else {
      toast({
        title: "Archived",
        description: "",
      });
    }
  };

  const onDuplicate = (row: any) => {
    const mz_adj_id = !!row.MzAdj ? uuidV4() : undefined;
    const add_ded_adj_id = !!row.AddDedAdj ? uuidV4() : undefined;
    const prepopulateData = {
      ...row,
      id: undefined,
      contract_no: undefined,
      mz_adj_id,
      add_ded_adj_id,
      AddDedAdj: add_ded_adj_id
        ? {
            ...row.AddDedAdj,
            id: add_ded_adj_id,
          }
        : undefined,
      MzAdj: mz_adj_id
        ? {
            ...row.MzAdj,
            id: mz_adj_id,
          }
        : undefined,
    };
    createPopulateWith(prepopulateData);
    setIsCreateOpen(true);
  };

  const onDelete = async ({ id }: { id: string }) => {
    if (isArchiving) return;
    setIsArchiving(true);

    const result = await deleteGeneralContract({ id });
    setIsArchiving(false);

    if (!result.success) {
      toast({
        title: "Failed to delete contract",
        description: "",
      });
    } else {
      toast({
        title: "Deleted",
        description: "",
      });
    }
  };

  const confirmReviewPrompt = async ({ id }: { id: string }) => {
    setReviewModalId(id);
  };

  const onReview = async ({ id }: { id: string }) => {
    if (isReviewing) return;
    setIsReviewing(true);

    try {
      const result = await unstageGeneralContract({ id });

      if (!result.success) {
        throw result;
      }

      toast({
        title: "Mark Reviewed",
        description: "",
      });
      setReviewModalId(undefined);
    } catch (error) {
      toast({
        title: "Failed to mark contract as reviewed",
        description: "",
      });
    } finally {
      setIsReviewing(false);
    }
  };

  return {
    isArchiving,
    isReviewing,
    reviewModalId,
    setReviewModalId,
    isCreateOpen,
    setIsCreateOpen,
    isUpdateOpen,
    setIsUpdateOpen,
    onArchive,
    onDuplicate,
    onDelete,
    onReview,
    confirmReviewPrompt,
    createPopulateWith,
    updatePopulateWith,
    createClearForm,
    updateClearForm,
  };
}

